import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { supabase } from "@/lib/supabase";
import { slugify, logActivity } from "@/lib/cms";
import { uploadImage } from "@/lib/storage";
import { useToast } from "@/context/ToastContext";
import type { Project, ProjectImage } from "@/lib/types";
import {
  Button,
  Field,
  Input,
  Textarea,
  Select,
  Toggle,
  Modal,
} from "@/pages/admin/components/ui";
import ImageUploader from "@/pages/admin/components/ImageUploader";
import TagInput from "@/pages/admin/components/TagInput";

const CATEGORIES = [
  "Web",
  "Sistemas",
  "Aplicaciones",
  "Comercio electrónico",
  "Automatización",
  "Empresarial",
  "Móvil",
  "Turismo",
];

interface Draft {
  nombre: string;
  cliente: string;
  categoria: string;
  categorias: string[];
  descripcion: string;
  descripcion_completa: string;
  problema: string;
  solucion: string;
  caracteristicas: string[];
  resultado: string;
  anio: string;
  url: string;
  url_demo: string;
  tecnologias: string[];
  imagen: string;
  logo: string;
  estado: string;
  destacado: boolean;
  orden: number;
  seo_title: string;
  seo_description: string;
  og_image: string;
}

function toDraft(p: Project | null): Draft {
  return {
    nombre: p?.nombre ?? "",
    cliente: p?.cliente ?? "",
    categoria: p?.categoria ?? "",
    categorias: p?.categorias ?? [],
    descripcion: p?.descripcion ?? "",
    descripcion_completa: p?.descripcion_completa ?? "",
    problema: p?.problema ?? "",
    solucion: p?.solucion ?? "",
    caracteristicas: p?.caracteristicas ?? [],
    resultado: p?.resultado ?? "",
    anio: p?.anio ?? String(new Date().getFullYear()),
    url: p?.url ?? "",
    url_demo: p?.url_demo ?? "",
    tecnologias: p?.tecnologias ?? [],
    imagen: p?.imagen ?? "",
    logo: p?.logo ?? "",
    estado: p?.estado ?? "borrador",
    destacado: p?.destacado ?? false,
    orden: p?.orden ?? 0,
    seo_title: p?.seo_title ?? "",
    seo_description: p?.seo_description ?? "",
    og_image: p?.og_image ?? "",
  };
}

export default function ProjectForm({
  project,
  onClose,
  onSaved,
}: {
  project: Project | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error: toastError } = useToast();
  const [draft, setDraft] = useState<Draft>(() => toDraft(project));
  const [gallery, setGallery] = useState<ProjectImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const galleryInput = useRef<HTMLInputElement>(null);

  const isEdit = Boolean(project);

  useEffect(() => {
    if (project?.id) {
      supabase
        .from("project_images")
        .select("*")
        .eq("project_id", project.id)
        .order("orden")
        .then(({ data }) => setGallery((data ?? []) as ProjectImage[]));
    }
  }, [project?.id]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function onGalleryFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingGallery(true);
    try {
      const { url } = await uploadImage(file, "proyectos");
      setGallery((g) => [...g, { id: "", project_id: project?.id ?? "", url, alt: "", orden: g.length, es_principal: false } as ProjectImage]);
    } catch {
      toastError("No se pudo subir la imagen de la galería.");
    } finally {
      setUploadingGallery(false);
    }
  }

  function moveGallery(index: number, dir: -1 | 1) {
    const next = [...gallery];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setGallery(next);
  }

  function removeGallery(id: string) {
    setGallery((g) => g.filter((x) => x.id !== id));
  }

  async function saveGallery(projectId: string) {
    const originalIds = new Set((project ? [] : []).map((x: ProjectImage) => x.id));
    const currentIds = new Set(gallery.map((g) => g.id).filter(Boolean));
    // eliminar imágenes quitadas (solo en edición)
    if (project) {
      const { data: existing } = await supabase
        .from("project_images")
        .select("id")
        .eq("project_id", projectId);
      for (const ex of existing ?? []) {
        if (!currentIds.has(ex.id)) {
          await supabase.from("project_images").delete().eq("id", ex.id);
        }
      }
    }
    void originalIds;
    for (let i = 0; i < gallery.length; i++) {
      const g = gallery[i];
      if (g.id) {
        await supabase.from("project_images").update({ url: g.url, alt: g.alt, orden: i }).eq("id", g.id);
      } else {
        await supabase
          .from("project_images")
          .insert({ project_id: projectId, url: g.url, alt: g.alt, orden: i });
      }
    }
  }

  async function save(publish: boolean) {
    if (!draft.nombre.trim()) {
      toastError("El nombre del proyecto es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: draft.nombre,
        cliente: draft.cliente || null,
        categoria: draft.categoria || null,
        categorias: draft.categorias,
        descripcion: draft.descripcion || null,
        descripcion_completa: draft.descripcion_completa || null,
        problema: draft.problema || null,
        solucion: draft.solucion || null,
        caracteristicas: draft.caracteristicas,
        resultado: draft.resultado || null,
        anio: draft.anio || null,
        url: draft.url || null,
        url_demo: draft.url_demo || null,
        tecnologias: draft.tecnologias,
        imagen: draft.imagen || null,
        logo: draft.logo || null,
        estado: publish ? "publicado" : draft.estado,
        destacado: draft.destacado,
        orden: draft.orden,
        seo_title: draft.seo_title || null,
        seo_description: draft.seo_description || null,
        og_image: draft.og_image || null,
      };

      let projectId = project?.id;
      if (projectId) {
        const { error } = await supabase.from("projects").update(payload).eq("id", projectId);
        if (error) throw error;
      } else {
        const slug = slugify(draft.nombre) || `proyecto-${Date.now()}`;
        const { data, error } = await supabase
          .from("projects")
          .insert({ ...payload, slug })
          .select("id")
          .single();
        if (error) throw error;
        projectId = data.id;
      }

      if (projectId) await saveGallery(projectId);

      await logActivity(
        isEdit ? "modificó el proyecto" : "creó el proyecto",
        draft.nombre
      );
      success(isEdit ? "Proyecto actualizado correctamente." : "Proyecto creado correctamente.");
      onSaved();
      onClose();
    } catch {
      toastError("No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Editar proyecto" : "Nuevo proyecto"}
      maxWidth="max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="secondary" onClick={() => save(false)} disabled={saving}>
            Guardar
          </Button>
          <Button onClick={() => save(true)} disabled={saving}>
            {saving ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Guardando…
              </>
            ) : (
              "Guardar y publicar"
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nombre" required>
            <Input value={draft.nombre} onChange={(e) => set("nombre", e.target.value)} />
          </Field>
          <Field label="Cliente">
            <Input value={draft.cliente} onChange={(e) => set("cliente", e.target.value)} />
          </Field>
          <Field label="Categoría principal">
            <Select value={draft.categoria} onChange={(e) => set("categoria", e.target.value)}>
              <option value="">Seleccionar…</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Año">
            <Input value={draft.anio} onChange={(e) => set("anio", e.target.value)} />
          </Field>
        </div>

        <Field label="Categorías (para filtros)">
          <TagInput value={draft.categorias} onChange={(v) => set("categorias", v)} />
        </Field>

        <Field label="Descripción corta">
          <Textarea rows={2} value={draft.descripcion} onChange={(e) => set("descripcion", e.target.value)} />
        </Field>

        <Field label="Descripción completa">
          <Textarea rows={3} value={draft.descripcion_completa} onChange={(e) => set("descripcion_completa", e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Problema que resuelve">
            <Textarea rows={3} value={draft.problema} onChange={(e) => set("problema", e.target.value)} />
          </Field>
          <Field label="Solución desarrollada">
            <Textarea rows={3} value={draft.solucion} onChange={(e) => set("solucion", e.target.value)} />
          </Field>
        </div>

        <Field label="Resultado">
          <Textarea rows={2} value={draft.resultado} onChange={(e) => set("resultado", e.target.value)} />
        </Field>

        <Field label="Características principales">
          <TagInput value={draft.caracteristicas} onChange={(v) => set("caracteristicas", v)} />
        </Field>

        <Field label="Tecnologías">
          <TagInput value={draft.tecnologias} onChange={(v) => set("tecnologias", v)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="URL del proyecto">
            <Input value={draft.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="URL de demostración">
            <Input value={draft.url_demo} onChange={(e) => set("url_demo", e.target.value)} placeholder="https://…" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImageUploader label="Imagen principal" value={draft.imagen} onChange={(v) => set("imagen", v)} folder="proyectos" />
          <ImageUploader label="Logo del cliente" value={draft.logo} onChange={(v) => set("logo", v)} folder="proyectos" aspect="aspect-square" />
        </div>

        <div className="flex flex-wrap items-center gap-6 pt-2">
          <Field label="Estado">
            <Select value={draft.estado} onChange={(e) => set("estado", e.target.value)}>
              <option value="borrador">Borrador</option>
              <option value="publicado">Publicado</option>
              <option value="oculto">Oculto</option>
            </Select>
          </Field>
          <Field label="Orden">
            <Input
              type="number"
              value={draft.orden}
              onChange={(e) => set("orden", Number(e.target.value))}
              className="w-24"
            />
          </Field>
          <div className="pt-6">
            <Toggle checked={draft.destacado} onChange={(v) => set("destacado", v)} label="Proyecto destacado" />
          </div>
        </div>

        <div className="border-t border-background-100 pt-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-3">SEO del proyecto</h4>
          <div className="space-y-3">
            <Field label="SEO title">
              <Input value={draft.seo_title} onChange={(e) => set("seo_title", e.target.value)} />
            </Field>
            <Field label="SEO description">
              <Textarea rows={2} value={draft.seo_description} onChange={(e) => set("seo_description", e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="border-t border-background-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground-900">Galería</h4>
            <button
              type="button"
              onClick={() => galleryInput.current?.click()}
              disabled={uploadingGallery}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              {uploadingGallery ? (
                <span className="w-4 h-4 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
              ) : (
                <i className="ri-image-add-line" />
              )}
              Añadir imagen
            </button>
            <input ref={galleryInput} type="file" accept="image/*" onChange={onGalleryFile} className="hidden" />
          </div>
          {gallery.length === 0 ? (
            <p className="text-xs text-foreground-400">Sin imágenes en la galería todavía.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((g, i) => (
                <div key={g.id || i} className="relative rounded-lg overflow-hidden border border-background-200 aspect-[16/10] group">
                  <img src={g.url} alt={g.alt || `galería ${i + 1}`} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/40 transition-colors" />
                  <div className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => moveGallery(i, -1)} disabled={i === 0} className="w-7 h-7 flex items-center justify-center rounded bg-white/90 text-foreground-700 disabled:opacity-40">
                      <i className="ri-arrow-left-line" />
                    </button>
                    <button type="button" onClick={() => moveGallery(i, 1)} disabled={i === gallery.length - 1} className="w-7 h-7 flex items-center justify-center rounded bg-white/90 text-foreground-700 disabled:opacity-40">
                      <i className="ri-arrow-right-line" />
                    </button>
                    <button type="button" onClick={() => removeGallery(g.id)} className="w-7 h-7 flex items-center justify-center rounded bg-red-600 text-white">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}